import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "./LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    setEnquiries(null);
    setVisits(null);
    axios
      .get(`${API}/my/enquiries`, { withCredentials: true })
      .then((r) => setEnquiries(r.data))
      .catch(() => setEnquiries([]));
    axios
      .get(`${API}/my/visits`, { withCredentials: true })
      .then((r) => setVisits(r.data))
      .catch(() => setVisits([]));
  }, [open, user]);

  return (
    <div className="relative">
      <button
        data-testid="nav-account-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
        className="inline-flex items-center justify-center rounded-full"
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user?.name || "Account"}
            referrerPolicy="no-referrer"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/70 hover:ring-gold transition-shadow duration-300"
          />
        ) : (
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full ring-2 ring-gold/70 hover:ring-gold transition-shadow duration-300">
            <LogoMark className="h-6 w-6" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              data-testid="account-menu-panel"
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-full mt-3 z-50 w-80 rounded-2xl border border-sand bg-white p-6 shadow-[0_24px_60px_-24px_rgba(15,56,44,0.45)]"
            >
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/60"
                  />
                ) : (
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full ring-2 ring-gold/60">
                    <LogoMark className="h-6 w-6" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="font-serif text-lg text-pine leading-tight truncate">
                    {user?.name || "Adveshta member"}
                  </p>
                  <p className="text-xs text-sage truncate">{user?.email}</p>
                </div>
              </div>

              <div className="mt-5 border-t border-sand pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                  Your enquiries
                </p>
                {enquiries === null ? (
                  <p className="mt-3 text-sm text-sage">Loading…</p>
                ) : enquiries.length === 0 ? (
                  <p className="mt-3 text-sm text-sage leading-relaxed">
                    No enquiries yet — use the contact form and they’ll appear here.
                  </p>
                ) : (
                  <ul className="mt-3 grid gap-2.5 max-h-56 overflow-y-auto">
                    {enquiries.slice(0, 5).map((e, i) => (
                      <li
                        key={e.id || i}
                        data-testid={`account-enquiry-item-${i}`}
                        className="rounded-xl bg-parchment border border-sand px-4 py-3"
                      >
                        <p className="text-sm font-medium text-coal">{e.interest}</p>
                        <p className="mt-0.5 text-xs text-sage">{fmt(e.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4 border-t border-sand pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                  Your farm visits
                </p>
                {visits === null ? (
                  <p className="mt-3 text-sm text-sage">Loading…</p>
                ) : visits.length === 0 ? (
                  <p className="mt-3 text-sm text-sage leading-relaxed">
                    No visits booked — weekends at the farm are open for advance booking.
                  </p>
                ) : (
                  <ul className="mt-3 grid gap-2.5 max-h-40 overflow-y-auto">
                    {visits.slice(0, 4).map((v, i) => (
                      <li
                        key={v.id || i}
                        data-testid={`account-visit-item-${i}`}
                        className="rounded-xl bg-parchment border border-sand px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-coal">{fmt(v.visit_date)}</p>
                          <p className="mt-0.5 text-xs text-sage">
                            {v.visitors} guest{v.visitors > 1 ? "s" : ""}
                            {v.meals ? " · meals" : ""}
                            {v.night_stay ? " · night stay" : ""}
                          </p>
                        </div>
                        <span className="rounded-full bg-gold/20 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-pine">
                          {v.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {user?.is_admin && (
                <Link
                  to="/admin"
                  data-testid="account-admin-link"
                  onClick={() => setOpen(false)}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold text-ink px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] hover:bg-pine hover:text-cream transition-colors duration-300"
                >
                  <LayoutDashboard className="h-3.5 w-3.5" /> Farm admin panel
                </Link>
              )}

              <button
                data-testid="account-signout-button"
                onClick={() => signOut()}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-pine/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-pine hover:bg-pine hover:text-cream transition-colors duration-300"
              >
                <LogOut className="h-3.5 w-3.5" /> Sign out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
